import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { useTenantDirectory } from "@/api/tenants";

/**
 * Route guard for platform-only screens (MPPI-00320).
 *
 * `RequirePermission` checks permissions and never scope, so a TENANT super
 * admin — who holds SUPER_ADMIN — rendered `/settings` and `/data-quality`
 * client-side. The API 403s them, so no data leaked, but the **JavaScript
 * chunk did**: `Settings.tsx` carries `SOURCE_LABELS`, a map of ~95
 * human-readable data-source names. A blank page whose bundle names every
 * portal we scrape is not a closed door.
 *
 * Gating the ROUTE means the lazy chunk is never fetched, so the vocabulary
 * never reaches the browser. That is the actual fix; hiding the menu item was
 * only the sign on the door.
 *
 * Renders nothing until scope resolves — showing the page and redirecting
 * after would fetch the chunk anyway, which is the thing being prevented.
 */
export function RequirePlatform({ children }: { children: ReactNode }) {
  const { isPlatform, isLoading } = useTenantDirectory();
  if (isLoading) return null;
  if (!isPlatform) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Scope for conditional rendering inside a page. */
export function useIsPlatform(): boolean {
  return useTenantDirectory().isPlatform;
}
